import { Injectable } from '@angular/core';
import { RequestService } from './htttp/request.service';
import { environment } from 'src/environments/environment';
import { Constants } from '../../shared/constant/constants';
import { Observable } from 'rxjs';

const CHECK_INTERVAL_KEY = 'UpdateCheckIntervalMinutes';
const CRON_HOUR_KEY = 'UPDATE_NOTICE_CRON_HOUR';

@Injectable({
  providedIn: 'root',
})
export class UpdateNoticeService {
  constructor(private requestService: RequestService) {}

  private get checkIntervalMinutes(): number {
    return parseInt(localStorage.getItem(CHECK_INTERVAL_KEY) ?? '0', 10) || 0;
  }

  /**
   * UTC hour at which the once-a-day check resets, as reported by the backend.
   *
   * Defaults to 5 (05:00 UTC = 1 AM Eastern) rather than 0, so even the fallback lands outside
   * school hours — UTC midnight is 7 PM US Central, which is the worst possible moment.
   */
  private get cronHour(): number {
    const raw = localStorage.getItem(CRON_HOUR_KEY);
    const parsed = raw === null ? NaN : parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= 23 ? parsed : 5;
  }

  initEnvAppVerison() {
    const appVersion = environment.appVersion;
    const localStoredAppVersion = localStorage.getItem('APP_VERSION');
    const lastUpdateNoticeCheckDate = localStorage.getItem('LastUpdateCheckDate');
    if (localStoredAppVersion == null || lastUpdateNoticeCheckDate == null) {
      localStorage.setItem('APP_VERSION', appVersion);
      localStorage.setItem('LastUpdateCheckDate', new Date().toISOString());
    }
  }

  isVersionDueForCheck(): boolean {
    const locallyStoredAppVersionDate: any = localStorage.getItem('LastUpdateCheckDate');
    const intervalMinutes = this.checkIntervalMinutes;

    if (intervalMinutes > 0) {
      if (!locallyStoredAppVersionDate) return true;
      const lastCheckedAt = new Date(locallyStoredAppVersionDate).getTime();
      if (!Number.isFinite(lastCheckedAt)) return true;
      const elapsedMs = Date.now() - lastCheckedAt;
      // A device that NTP-syncs late after waking can leave a future-dated stamp; one check
      // rewrites it rather than stalling forever.
      if (elapsedMs < 0) return true;
      return elapsedMs >= intervalMinutes * 60 * 1000;
    }

    const tempLocallyStoredAppVersionDate = new Date(locallyStoredAppVersionDate);
    if (!Number.isFinite(tempLocallyStoredAppVersionDate.getTime())) return true;

    // The "update day" starts at cronHour UTC instead of UTC midnight, so the daily reset lands at
    // a time that is outside school hours in the US, Australia and England.
    const cronHour = this.cronHour;
    const getUpdateDay = (date: Date) => {
      const d = new Date(date.getTime());
      if (d.getUTCHours() < cronHour) {
        d.setUTCDate(d.getUTCDate() - 1);
      }
      d.setUTCHours(0, 0, 0, 0);
      return d.getTime();
    };

    return getUpdateDay(tempLocallyStoredAppVersionDate) < getUpdateDay(new Date());
  }
  apiVersionCallCheck(): Observable<any> {
    return this.requestService.post(Constants.ENDPOINTS.getAppVersion, {});
  }

  updateLocalAppVersion(appVersion: string, intervalMinutes = 0, cronHour = 5) {
    localStorage.setItem('APP_VERSION', appVersion);

    localStorage.setItem('LastUpdateCheckDate', new Date().toISOString());

    localStorage.setItem(CRON_HOUR_KEY, String(cronHour));

    if (intervalMinutes > 0) {
      localStorage.setItem(CHECK_INTERVAL_KEY, String(intervalMinutes));
    } else {
      localStorage.removeItem(CHECK_INTERVAL_KEY);
    }
  }
}
